import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api, { analyticsApi } from '../services/api';
import ToastReportsPage from './ToastReportsPage';
import {
  BarChart3,
  Download,
  Calendar,
  DollarSign,
  Users,
  Clock,
  TrendingUp,
  FileText,
  Loader2,
  UtensilsCrossed,
} from 'lucide-react';
import { format, subDays } from 'date-fns';
import clsx from 'clsx';

type ReportType = 'sales' | 'labor' | 'menu' | 'kitchen' | 'server';

interface ReportConfig {
  id: ReportType;
  name: string;
  description: string;
  icon: typeof BarChart3;
  category: 'sales' | 'labor' | 'operations';
}

const availableReports: ReportConfig[] = [
  {
    id: 'sales',
    name: 'Sales Report',
    description: 'Revenue, orders, and daily sales breakdown',
    icon: DollarSign,
    category: 'sales',
  },
  {
    id: 'menu',
    name: 'Menu Performance',
    description: 'Best sellers, item revenue, and product mix',
    icon: UtensilsCrossed,
    category: 'sales',
  },
  {
    id: 'labor',
    name: 'Labor Report',
    description: 'Hours worked by employee, role, and day',
    icon: Users,
    category: 'labor',
  },
  {
    id: 'kitchen',
    name: 'Kitchen Performance',
    description: 'Ticket times, orders by hour, station breakdown',
    icon: Clock,
    category: 'operations',
  },
  {
    id: 'server',
    name: 'Server Performance',
    description: 'Sales, tips, and check averages by server',
    icon: TrendingUp,
    category: 'operations',
  },
];

function ClassicReportsPage() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedReport, setSelectedReport] = useState<ReportType | null>(null);
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  });

  const categories = [
    { id: 'all', label: 'All Reports' },
    { id: 'sales', label: 'Sales' },
    { id: 'labor', label: 'Labor' },
    { id: 'operations', label: 'Operations' },
  ];

  const filteredReports = availableReports.filter(
    (report) => selectedCategory === 'all' || report.category === selectedCategory
  );

  // Fetch sales report
  const { data: salesData, isLoading: salesLoading } = useQuery({
    queryKey: ['report-sales', dateRange],
    queryFn: async () => {
      const response = await analyticsApi.getSalesReport({
        startDate: dateRange.start,
        endDate: dateRange.end,
      });
      return response.data?.data || response.data;
    },
    enabled: selectedReport === 'sales',
  });

  // Fetch menu performance
  const { data: menuData, isLoading: menuLoading } = useQuery({
    queryKey: ['report-menu', dateRange],
    queryFn: async () => {
      const response = await analyticsApi.getMenuPerformance({
        days: Math.ceil((new Date(dateRange.end).getTime() - new Date(dateRange.start).getTime()) / (1000 * 60 * 60 * 24)),
      });
      return response.data?.data || response.data;
    },
    enabled: selectedReport === 'menu',
  });

  // Fetch labor report
  const { data: laborData, isLoading: laborLoading } = useQuery({
    queryKey: ['report-labor', dateRange],
    queryFn: async () => {
      const response = await analyticsApi.getLaborReport({
        startDate: dateRange.start,
        endDate: dateRange.end,
      });
      return response.data?.data || response.data;
    },
    enabled: selectedReport === 'labor',
  });

  // Fetch kitchen performance
  const { data: kitchenData, isLoading: kitchenLoading } = useQuery({
    queryKey: ['report-kitchen', dateRange],
    queryFn: async () => {
      const response = await analyticsApi.getKitchenPerformance({
        startDate: dateRange.start,
        endDate: dateRange.end,
      });
      return response.data?.data || response.data;
    },
    enabled: selectedReport === 'kitchen',
  });

  // Fetch server performance
  const { data: serverData, isLoading: serverLoading } = useQuery({
    queryKey: ['report-server', dateRange],
    queryFn: async () => {
      const response = await analyticsApi.getServerPerformance({
        startDate: dateRange.start,
        endDate: dateRange.end,
      });
      return response.data?.data || response.data;
    },
    enabled: selectedReport === 'server',
  });

  const isLoading = salesLoading || menuLoading || laborLoading || kitchenLoading || serverLoading;

  const formatCurrency = (cents: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

  const handleGenerateReport = (reportId: ReportType) => {
    setSelectedReport(reportId);
  };

  const handleDownload = () => {
    // Create a simple CSV download based on the current report data
    let csvContent = '';
    let filename = '';

    if (selectedReport === 'sales' && salesData) {
      filename = `sales-report-${dateRange.start}-to-${dateRange.end}.csv`;
      csvContent = 'Date,Revenue,Check Count,Average Check\n';
      salesData.salesByPeriod?.forEach((row: any) => {
        csvContent += `${row.period},${(row.revenue / 100).toFixed(2)},${row.checkCount},${(row.averageCheck / 100).toFixed(2)}\n`;
      });
    } else if (selectedReport === 'labor' && laborData) {
      filename = `labor-report-${dateRange.start}-to-${dateRange.end}.csv`;
      csvContent = 'Employee,Role,Total Hours,Shifts\n';
      laborData.byEmployee?.forEach((row: any) => {
        csvContent += `${row.employeeName},${row.role},${row.totalHours.toFixed(1)},${row.shifts}\n`;
      });
    } else if (selectedReport === 'menu' && menuData) {
      filename = `menu-report-${dateRange.start}-to-${dateRange.end}.csv`;
      csvContent = 'Item,Quantity Sold,Revenue\n';
      menuData.topSellers?.forEach((row: any) => {
        csvContent += `${row.name},${row.quantity},${(row.revenue / 100).toFixed(2)}\n`;
      });
    }

    if (csvContent) {
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const renderReportContent = () => {
    if (!selectedReport) return null;

    if (isLoading) {
      return (
        <div className="card p-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary-600" />
          <p className="mt-4 text-slate-500">Generating report...</p>
        </div>
      );
    }

    switch (selectedReport) {
      case 'sales':
        return (
          <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="card p-4">
                <p className="text-sm text-slate-500">Total Revenue</p>
                <p className="text-2xl font-bold">{formatCurrency(salesData?.summary?.totalRevenue || 0)}</p>
              </div>
              <div className="card p-4">
                <p className="text-sm text-slate-500">Total Checks</p>
                <p className="text-2xl font-bold">{salesData?.summary?.totalChecks || 0}</p>
              </div>
              <div className="card p-4">
                <p className="text-sm text-slate-500">Average Check</p>
                <p className="text-2xl font-bold">{formatCurrency(salesData?.summary?.averageCheck || 0)}</p>
              </div>
            </div>

            {/* Daily breakdown */}
            <div className="card overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex justify-between items-center">
                <h3 className="font-semibold">Sales by Day</h3>
                <button onClick={handleDownload} className="btn btn-secondary btn-sm flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Download CSV
                </button>
              </div>
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="table-header">Date</th>
                    <th className="table-header text-right">Revenue</th>
                    <th className="table-header text-right">Checks</th>
                    <th className="table-header text-right">Avg Check</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {salesData?.salesByPeriod?.map((row: any) => (
                    <tr key={row.period} className="hover:bg-slate-50">
                      <td className="table-cell font-medium">{row.period}</td>
                      <td className="table-cell text-right">{formatCurrency(row.revenue)}</td>
                      <td className="table-cell text-right">{row.checkCount}</td>
                      <td className="table-cell text-right">{formatCurrency(row.averageCheck)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!salesData?.salesByPeriod || salesData.salesByPeriod.length === 0) && (
                <div className="p-8 text-center text-slate-500">No sales data for this period</div>
              )}
            </div>
          </div>
        );

      case 'menu':
        return (
          <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="card p-4">
                <p className="text-sm text-slate-500">Items Sold</p>
                <p className="text-2xl font-bold">{menuData?.totalItemsSold || 0}</p>
              </div>
              <div className="card p-4">
                <p className="text-sm text-slate-500">Unique Items</p>
                <p className="text-2xl font-bold">{menuData?.uniqueItems || 0}</p>
              </div>
            </div>

            {/* Top sellers */}
            <div className="card overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex justify-between items-center">
                <h3 className="font-semibold">Top Sellers</h3>
                <button onClick={handleDownload} className="btn btn-secondary btn-sm flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Download CSV
                </button>
              </div>
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="table-header">Item</th>
                    <th className="table-header text-right">Quantity</th>
                    <th className="table-header text-right">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {menuData?.topSellers?.map((item: any, i: number) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="table-cell font-medium">{item.name}</td>
                      <td className="table-cell text-right">{item.quantity}</td>
                      <td className="table-cell text-right">{formatCurrency(item.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!menuData?.topSellers || menuData.topSellers.length === 0) && (
                <div className="p-8 text-center text-slate-500">No menu data for this period</div>
              )}
            </div>
          </div>
        );

      case 'labor':
        return (
          <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="card p-4">
                <p className="text-sm text-slate-500">Total Hours</p>
                <p className="text-2xl font-bold">{laborData?.summary?.totalHours?.toFixed(1) || 0}</p>
              </div>
              <div className="card p-4">
                <p className="text-sm text-slate-500">Total Shifts</p>
                <p className="text-2xl font-bold">{laborData?.summary?.totalShifts || 0}</p>
              </div>
              <div className="card p-4">
                <p className="text-sm text-slate-500">Employees</p>
                <p className="text-2xl font-bold">{laborData?.summary?.uniqueEmployees || 0}</p>
              </div>
            </div>

            {/* By employee */}
            <div className="card overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex justify-between items-center">
                <h3 className="font-semibold">Hours by Employee</h3>
                <button onClick={handleDownload} className="btn btn-secondary btn-sm flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Download CSV
                </button>
              </div>
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="table-header">Employee</th>
                    <th className="table-header">Role</th>
                    <th className="table-header text-right">Hours</th>
                    <th className="table-header text-right">Shifts</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {laborData?.byEmployee?.map((emp: any) => (
                    <tr key={emp.employeeId} className="hover:bg-slate-50">
                      <td className="table-cell font-medium">{emp.employeeName}</td>
                      <td className="table-cell capitalize">{emp.role}</td>
                      <td className="table-cell text-right">{emp.totalHours.toFixed(1)}</td>
                      <td className="table-cell text-right">{emp.shifts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!laborData?.byEmployee || laborData.byEmployee.length === 0) && (
                <div className="p-8 text-center text-slate-500">No labor data for this period</div>
              )}
            </div>
          </div>
        );

      case 'kitchen':
        return (
          <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="card p-4">
                <p className="text-sm text-slate-500">Total Tickets</p>
                <p className="text-2xl font-bold">{kitchenData?.ticketMetrics?.totalTickets || 0}</p>
              </div>
              <div className="card p-4">
                <p className="text-sm text-slate-500">Avg Ticket Time</p>
                <p className="text-2xl font-bold">{kitchenData?.ticketMetrics?.averageTicketTime || 0} min</p>
              </div>
              <div className="card p-4">
                <p className="text-sm text-slate-500">Min Time</p>
                <p className="text-2xl font-bold">{kitchenData?.ticketMetrics?.minTicketTime || 0} min</p>
              </div>
              <div className="card p-4">
                <p className="text-sm text-slate-500">Max Time</p>
                <p className="text-2xl font-bold">{kitchenData?.ticketMetrics?.maxTicketTime || 0} min</p>
              </div>
            </div>

            {/* Orders by hour */}
            <div className="card p-4">
              <h3 className="font-semibold mb-4">Orders by Hour</h3>
              <div className="flex items-end gap-1 h-32">
                {Array.from({ length: 24 }, (_, h) => {
                  const data = kitchenData?.ordersByHour?.find((d: any) => d.hour === h);
                  const count = data?.count || 0;
                  const maxCount = Math.max(...(kitchenData?.ordersByHour?.map((d: any) => d.count) || [1]));
                  const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                  return (
                    <div key={h} className="flex-1 flex flex-col items-center">
                      <div
                        className="w-full bg-primary-500 rounded-t"
                        style={{ height: `${height}%`, minHeight: count > 0 ? 4 : 0 }}
                        title={`${h}:00 - ${count} orders`}
                      />
                      {h % 4 === 0 && <span className="text-xs text-slate-400 mt-1">{h}</span>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* By station */}
            <div className="card overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <h3 className="font-semibold">Items by Station</h3>
              </div>
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="table-header">Station</th>
                    <th className="table-header text-right">Items</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {kitchenData?.itemsByStation?.map((s: any) => (
                    <tr key={s.station} className="hover:bg-slate-50">
                      <td className="table-cell font-medium capitalize">{s.station}</td>
                      <td className="table-cell text-right">{s.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'server':
        return (
          <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="card p-4">
                <p className="text-sm text-slate-500">Total Sales</p>
                <p className="text-2xl font-bold">{formatCurrency(serverData?.summary?.totalSales || 0)}</p>
              </div>
              <div className="card p-4">
                <p className="text-sm text-slate-500">Total Tips</p>
                <p className="text-2xl font-bold">{formatCurrency(serverData?.summary?.totalTips || 0)}</p>
              </div>
              <div className="card p-4">
                <p className="text-sm text-slate-500">Servers</p>
                <p className="text-2xl font-bold">{serverData?.summary?.totalServers || 0}</p>
              </div>
            </div>

            {/* By server */}
            <div className="card overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <h3 className="font-semibold">Performance by Server</h3>
              </div>
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="table-header">Server</th>
                    <th className="table-header text-right">Checks</th>
                    <th className="table-header text-right">Sales</th>
                    <th className="table-header text-right">Tips</th>
                    <th className="table-header text-right">Avg Check</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {serverData?.servers?.map((s: any) => (
                    <tr key={s.serverId} className="hover:bg-slate-50">
                      <td className="table-cell font-medium">{s.serverName}</td>
                      <td className="table-cell text-right">{s.checks}</td>
                      <td className="table-cell text-right">{formatCurrency(s.totalSales)}</td>
                      <td className="table-cell text-right">{formatCurrency(s.totalTips)}</td>
                      <td className="table-cell text-right">{formatCurrency(s.averageCheck)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!serverData?.servers || serverData.servers.length === 0) && (
                <div className="p-8 text-center text-slate-500">No server data for this period</div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
          <p className="text-slate-500">Generate and analyze business reports</p>
        </div>
        {selectedReport && (
          <button onClick={() => setSelectedReport(null)} className="btn btn-secondary">
            Back to Reports
          </button>
        )}
      </div>

      {/* Date Range Selector */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-slate-500" />
            <span className="text-sm font-medium">Date Range:</span>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="input w-auto"
            />
            <span className="text-slate-500">to</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="input w-auto"
            />
          </div>
          <div className="flex gap-2 ml-auto">
            {[7, 30, 90].map((days) => (
              <button
                key={days}
                onClick={() =>
                  setDateRange({
                    start: format(subDays(new Date(), days), 'yyyy-MM-dd'),
                    end: format(new Date(), 'yyyy-MM-dd'),
                  })
                }
                className="btn btn-secondary text-sm"
              >
                Last {days} days
              </button>
            ))}
          </div>
        </div>
      </div>

      {!selectedReport ? (
        <>
          {/* Category Tabs */}
          <div className="flex gap-2 overflow-x-auto">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={clsx(
                  'px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors',
                  selectedCategory === category.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-100'
                )}
              >
                {category.label}
              </button>
            ))}
          </div>

          {/* Reports Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredReports.map((report) => {
              const Icon = report.icon;
              return (
                <div key={report.id} className="card p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-lg">{report.name}</h3>
                      <p className="text-sm text-slate-500 mt-1">{report.description}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleGenerateReport(report.id)}
                    className="btn btn-primary w-full mt-4 flex items-center justify-center gap-2"
                  >
                    <BarChart3 className="w-4 h-4" />
                    View Report
                  </button>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        renderReportContent()
      )}
    </div>
  );
}


// POS-connected venues get analytics computed from the live feed; classic
// Mise-POS restaurants keep the check-based reports. Gate is its own
// component so each branch keeps a stable hook order.
export default function ReportsPage() {
  const { data: toastStatus, isLoading } = useQuery({
    queryKey: ['toast-status-gate'],
    queryFn: async () => (await api.get('/v1/integrations/toast/status')).data?.data,
    staleTime: 60_000,
  });
  const { data: insight } = useQuery({
    queryKey: ['insight-days-gate'],
    queryFn: async () => (await api.get('/v1/integrations/toast/insight-days')).data?.data,
    staleTime: 60_000,
  });
  // Account-level POS signal: a multi-store owner whose signed-in store is a
  // commissary hub (no feed of its own) still has real sales across the chain —
  // item-names('all') returns a non-empty list in exactly that case, so the
  // Toast reports (and the cross-store item lookup) show for them too.
  const { data: accountNames } = useQuery({
    queryKey: ['item-names-gate'],
    queryFn: async () => (await api.get('/v1/integrations/toast/item-names', { params: { restaurantId: 'all', period: 'last30' } })).data?.data,
    staleTime: 60_000,
  });
  if (isLoading) return null;
  const accountHasPos = (accountNames?.names?.length || 0) > 0;
  if (toastStatus?.connected || insight?.connected || accountHasPos) return <ToastReportsPage />;
  return <ClassicReportsPage />;
}
